import {
  ContainerDNUChartForm,
  ContainerDNUChartFormValues,
  ContainerDNUStatisticsByTimeChart,
  ContainerDNUStatisticsSearchToolbar,
  ResizeableContent,
  RouteBreadcrumbs,
  ThemedPage,
  useApp
} from "@ots-frontend/ots-ui";
import {Card, Container, Grid, Group, LoadingOverlay, Menu, rem, Text, Title, UnstyledButton} from "@mantine/core";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {
  adjustToTimeZone,
  cargoSubtype,
  ContainerDNUStatisticsSearchCriteria,
  containerDNUStatisticsSelector,
  containsValue,
  EntitySearchCriteria,
  getDefaultTimeFrame,
  transportMethod,
  TransportMethod,
  TimeGroup,
  timeGroup,
  useSearchContainerDNUStatisticsQuery
} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {useDispatch} from "react-redux";
import {getContainerDNUStatisticsSearchPageFilter} from "../../store/container-dnu-statistics-search-page/selectors";
import {setContainerDNUStatisticsSearchPageFilter} from "../../store/container-dnu-statistics-search-page/actions";

export const ContainerDNUStatisticsSearchPage = () => {

  const dispatch = useDispatch()
  const {t} = useTranslation(["translation", "common"]);
  const {regionId, appCategory} = useApp()

  const filter = useAppSelector(getContainerDNUStatisticsSearchPageFilter)

  const [haulMethods, setHaulMethods] = useState<TransportMethod[]>([transportMethod.trucking, transportMethod.pipeline])
  const [splitHaulMethods, setSplitHaulMethods] = useState<boolean | undefined>(true)

  const selectContainerDNUStatistics = useMemo(() => {
    return containerDNUStatisticsSelector()
  }, []);

  const volumeInventoryCriteria: ContainerDNUStatisticsSearchCriteria | any = useMemo(() => {
    if (filter?.fromDttm && regionId) {
      const defaultTimeFrame = getDefaultTimeFrame(filter.fromDttm, filter.toDttm)

      let criteriaTimeGroup: TimeGroup
      if (defaultTimeFrame === "MONTH" || defaultTimeFrame === "WEEK" || defaultTimeFrame === "DAY") {
        criteriaTimeGroup = timeGroup.daily
      } else {
        criteriaTimeGroup = timeGroup.hourly
      }

      return {
        fromDttm: adjustToTimeZone(filter.fromDttm).getTime(),
        toDttm: filter.toDttm ? adjustToTimeZone(filter.toDttm).getTime() : undefined,
        timeGroup: criteriaTimeGroup.code,
        regionId: regionId,
        cargoSubtypes: [(appCategory?.cargoSubtype ?? cargoSubtype.water).code],
        entityCriteria: filter.entityCriteria,
      }
    } else {
      return skipToken
    }
  }, [filter?.fromDttm, filter?.toDttm, filter?.entityCriteria, regionId, appCategory?.cargoSubtype])

  const {snapshots, isLoading, refetch} = useSearchContainerDNUStatisticsQuery(volumeInventoryCriteria, {
    selectFromResult: (result) => ({
      ...result,
      snapshots: selectContainerDNUStatistics(result),
      isLoading: result.isLoading || result.isFetching,
    })
  });

  const onFilterChange = useCallback((value: {
    fromDate: Date,
    toDate: Date | undefined,
    entityType: string | undefined,
    entityIds: string[] | undefined
  }) => {
    let entityCriteria: EntitySearchCriteria | undefined = undefined
    if (value.entityType) {
      entityCriteria = {
        ids: value.entityIds,
        type: value.entityType
      }
    }

    dispatch(setContainerDNUStatisticsSearchPageFilter({
      fromDttm: value.fromDate.getTime(),
      toDttm: value.toDate?.getTime(),
      entityCriteria: entityCriteria
    }));
  }, [dispatch]);

  const onChartOptionsChange = useCallback((values: ContainerDNUChartFormValues) => {
    const methods: TransportMethod[] = []

    if (values.includeTrucking) {
      methods.push(transportMethod.trucking)
    }

    if (values.includePipeline) {
      methods.push(transportMethod.pipeline)
    }

    setHaulMethods(methods)
    setSplitHaulMethods(values.splitHaulMethods)
  }, [])

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <RouteBreadcrumbs p="sm"/>
              <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                <Group position="apart">
                  <Title
                    order={4}>{t("page.containerdnusearch.title", "Usable Containers", {ns: "translation"})}</Title>
                  <Menu
                    width={260}
                    position="bottom-end"
                    transitionProps={{transition: "pop-top-right"}}
                    withinPortal
                  >
                    <Menu.Target>
                      <UnstyledButton>
                        <Group spacing={7}>
                          <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                            Actions
                          </Text>
                          <IconChevronDown size={rem(12)} stroke={1.5}/>
                        </Group>
                      </UnstyledButton>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item onClick={() => refetch()}>
                        {t("commons.action.refresh", "Refresh", {ns: "common"})}
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Group>
                <ContainerDNUStatisticsSearchToolbar
                  defaultValues={{
                    fromDate: filter?.fromDttm ? new Date(filter?.fromDttm) : undefined,
                    toDate: filter?.toDttm ? new Date(filter?.toDttm) : undefined,
                    entityType: filter?.entityCriteria?.type,
                    entityIds: filter?.entityCriteria?.ids,
                  }}
                  loading={isLoading}
                  onSubmit={onFilterChange}/>
              </Card>

              <LoadingOverlay visible={isLoading}/>

              <Container p="md" fluid={true}>
                <Grid>
                  <Grid.Col span={8}>
                    <Container fluid={true}>
                      <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                        <ContainerDNUStatisticsByTimeChart
                          id={"search_container_dnu_by_time_chart"}
                          fromDttm={filter?.fromDttm ?? new Date().getTime()}
                          toDttm={filter?.toDttm}
                          snapshots={snapshots}
                          category={appCategory}
                          methods={haulMethods}
                          splitMethods={splitHaulMethods}/>
                      </Card>
                    </Container>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                      <ContainerDNUChartForm
                        defaultValues={{
                          includeTrucking: containsValue(haulMethods, transportMethod.trucking),
                          includePipeline: containsValue(haulMethods, transportMethod.pipeline),
                          splitHaulMethods: splitHaulMethods,
                        }}
                        onSubmit={onChartOptionsChange}/>
                    </Card>
                  </Grid.Col>
                </Grid>
              </Container>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
